/* oxlint-disable tailwindcss/no-unknown-classes -- Scrubber styles are colocated in media-tabs.css. */
import { SegmentedTabs } from "@ui/components/primitives/SegmentedTabs"
import { useMediaQuery } from "@ui/hooks/useMediaQuery"
import { Pause, Play } from "lucide-react"
import { type ChangeEvent, type CSSProperties, useEffect, useId, useRef, useState } from "react"

import { usePrefersReducedMotion } from "@/hooks/usePrefersReducedMotion"

import "./media-tabs.css"

interface MediaTabItem {
  label: string
  src: string
  poster?: string
  caption?: string
}

interface MediaTabsProps {
  items: MediaTabItem[]
  /** Accessible name for the switcher, e.g. the section the clips illustrate. */
  label: string
  /** Shown under every tab unless the active item has its own caption. */
  caption?: string
  /** CSS aspect-ratio for the frame. */
  aspect?: string
  /** Show the play/pause and seek bar. Off for ambient loops. */
  controls?: boolean
}

const PLAY_THRESHOLD = 0.4

function posterFor(src: string) {
  return src.replace(/\.mp4$/, ".jpg")
}

function formatTime(seconds: number) {
  const whole = Math.max(0, Math.floor(seconds))
  return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, "0")}`
}

function MediaVideo({
  src,
  poster,
  label,
  controls,
}: {
  src: string
  poster: string
  label: string
  controls: boolean
}) {
  const videoRef = useRef<HTMLVideoElement>(null)
  const [playing, setPlaying] = useState(false)
  const [time, setTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const pausedByUserRef = useRef(false)
  const reducedMotion = usePrefersReducedMotion()
  const [autoplayBlocked, setAutoplayBlocked] = useState(false)

  useEffect(() => {
    const video = videoRef.current
    if (!video) return
    // Listen natively: a server-rendered video can load its metadata before hydration.
    const syncDuration = () => setDuration(Number.isFinite(video.duration) ? video.duration : 0)
    syncDuration()
    video.addEventListener("durationchange", syncDuration)
    // Re-runs when the reader changes the setting: reduced motion stops autoplay.
    if (reducedMotion) video.pause()

    // Pause offscreen clips so a long post doesn't decode them all at once, and autoplay
    // on screen unless the reader prefers reduced motion or paused it themselves.
    const observer = new IntersectionObserver(
      ([entry]) => {
        // isIntersecting turns true at any overlap, so check the ratio against the threshold.
        const visible = !!entry && entry.intersectionRatio >= PLAY_THRESHOLD
        if (!visible) {
          video.pause()
        } else if (!reducedMotion && !pausedByUserRef.current) {
          video.play().catch((error: unknown) => {
            // Only a refusal (e.g. iOS Low Power Mode) means autoplay is blocked; a pause
            // during a quick scroll rejects with AbortError instead.
            if (error instanceof DOMException && error.name === "NotAllowedError") {
              setAutoplayBlocked(true)
            }
          })
        }
      },
      { threshold: PLAY_THRESHOLD },
    )
    observer.observe(video)
    return () => {
      observer.disconnect()
      video.removeEventListener("durationchange", syncDuration)
    }
  }, [reducedMotion])

  // timeupdate fires only a few times a second; follow frames while playing so the scrubber glides.
  useEffect(() => {
    const video = videoRef.current
    if (!video || !playing) return
    let frame = requestAnimationFrame(function tick() {
      setTime(video.currentTime)
      frame = requestAnimationFrame(tick)
    })
    return () => cancelAnimationFrame(frame)
  }, [playing])

  function togglePlayback() {
    const video = videoRef.current
    if (!video) return
    if (video.paused) {
      pausedByUserRef.current = false
      void video.play().catch(() => undefined)
    } else {
      pausedByUserRef.current = true
      video.pause()
    }
  }

  function seek(event: ChangeEvent<HTMLInputElement>) {
    const video = videoRef.current
    if (!video) return
    video.currentTime = Number(event.target.value)
    setTime(video.currentTime)
  }

  const progress = duration > 0 ? (time / duration) * 100 : 0

  return (
    <>
      <video
        ref={videoRef}
        src={src}
        poster={poster}
        aria-label={label}
        muted
        loop
        playsInline
        preload="metadata"
        className="absolute inset-0 size-full object-cover"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onTimeUpdate={(event) => setTime(event.currentTarget.currentTime)}
      />
      {/* Without controls the clip only autoplays. When reduced motion skips that, or the
          browser refuses it, readers get a play button instead. */}
      {!controls && (reducedMotion || autoplayBlocked) && (
        <button
          type="button"
          onClick={togglePlayback}
          aria-label={playing ? `Pause ${label}` : `Play ${label}`}
          className="absolute inset-0 flex cursor-pointer items-center justify-center focus-visible:outline-2 focus-visible:-outline-offset-4 focus-visible:outline-white"
        >
          <span className="flex size-12 items-center justify-center rounded-full bg-black/55 text-white backdrop-blur">
            {playing ? <Pause className="size-5" /> : <Play className="size-5 translate-x-px" />}
          </span>
        </button>
      )}
      {controls && (
        <div className="absolute inset-x-0 bottom-0 flex items-center gap-3 bg-linear-to-t from-black/60 to-transparent px-3 pt-8 pb-2.5 text-white sm:px-4 sm:pb-3">
          <button
            type="button"
            onClick={togglePlayback}
            aria-label={playing ? `Pause ${label}` : `Play ${label}`}
            className="flex size-7 shrink-0 cursor-pointer items-center justify-center rounded-full bg-white/15 backdrop-blur transition-colors hover:bg-white/25 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
          >
            {playing ? (
              <Pause className="size-3.5" />
            ) : (
              <Play className="size-3.5 translate-x-px" />
            )}
          </button>
          <input
            type="range"
            min={0}
            max={duration || 0}
            step="any"
            value={time}
            onChange={seek}
            aria-label={`Seek ${label}`}
            aria-valuetext={`${formatTime(time)} of ${formatTime(duration)}`}
            className="media-scrubber h-5 min-w-0 flex-1 cursor-pointer"
            style={{ "--media-progress": `${progress}%` } as CSSProperties}
          />
          <span className="shrink-0 font-mono text-xs text-white/85 tabular-nums">
            {formatTime(time)} / {formatTime(duration)}
          </span>
        </div>
      )}
    </>
  )
}

export function MediaTabs({
  items,
  label,
  caption,
  aspect = "16 / 9",
  controls = true,
}: MediaTabsProps) {
  const baseId = useId()
  const [activeIndex, setActiveIndex] = useState(0)
  // Most switchers are wider than a phone, so stack them there instead of scrolling sideways.
  const stacked = useMediaQuery("(max-width: 639px)")
  const active = items[activeIndex] ?? items[0]
  if (!active) return null

  const activeCaption = active.caption ?? caption
  const tabId = (index: number) => `${baseId}-tab-${index}`
  const panelId = `${baseId}-panel`

  return (
    <figure className="not-prose my-10 flex w-full flex-col items-center gap-4">
      {items.length > 1 && (
        <SegmentedTabs
          aria-label={label}
          orientation={stacked ? "vertical" : "horizontal"}
          fill={stacked}
          className={stacked ? "w-full" : undefined}
          items={items.map((item, index) => ({ id: String(index), label: item.label }))}
          value={String(activeIndex)}
          onValueChange={(value) => setActiveIndex(Number(value))}
          getTabId={(id) => tabId(Number(id))}
          getPanelId={() => panelId}
        />
      )}
      <div
        id={panelId}
        role={items.length > 1 ? "tabpanel" : undefined}
        aria-labelledby={items.length > 1 ? tabId(activeIndex) : undefined}
        className="relative w-full overflow-hidden rounded-lg bg-(--neutral-150) dark:bg-neutral-900"
        style={{ aspectRatio: aspect }}
      >
        <MediaVideo
          key={active.src}
          src={active.src}
          poster={active.poster ?? posterFor(active.src)}
          label={`${label}: ${active.label}`}
          controls={controls}
        />
      </div>
      {activeCaption && (
        <figcaption className="text-center text-sm leading-relaxed text-pretty text-muted italic">
          {activeCaption}
        </figcaption>
      )}
    </figure>
  )
}
